export const UNSAVED_CHANGES_MESSAGE =
    'Masz niezapisane zmiany. Czy na pewno chcesz opuścić tę stronę?';

interface DirtyFormState {
    scope: 'page' | 'modal';
    fields: Map<string, string>;
    guardPath: string;
}

const dirtyFieldsByForm = new Map<string, DirtyFormState>();
const snapshotBaselines = new Map<string, any>();
const snapshotGuardPaths = new Map<string, string>();
let nextGeneratedFormId = 0;

export const createUnsavedFormId = (prefix: string): string =>
    `${prefix}:${++nextGeneratedFormId}`;

const isWithinGuardPath = (pathname: string, guardPath: string): boolean =>
    pathname === guardPath || pathname.startsWith(`${guardPath}/`);

const areValuesEqual = (initialValue: any, currentValue: any): boolean => {
    if (Object.is(initialValue, currentValue)) return true;

    if (
        initialValue === null ||
        currentValue === null ||
        typeof initialValue !== 'object' ||
        typeof currentValue !== 'object'
    ) {
        return false;
    }

    if (initialValue instanceof Date || currentValue instanceof Date) {
        return (
            initialValue instanceof Date &&
            currentValue instanceof Date &&
            initialValue.getTime() === currentValue.getTime()
        );
    }

    if (
        typeof File !== 'undefined' &&
        (initialValue instanceof File || currentValue instanceof File)
    ) {
        return (
            initialValue instanceof File &&
            currentValue instanceof File &&
            initialValue.name === currentValue.name &&
            initialValue.size === currentValue.size &&
            initialValue.type === currentValue.type &&
            initialValue.lastModified === currentValue.lastModified
        );
    }

    if (
        Array.isArray(initialValue) !== Array.isArray(currentValue) ||
        Object.getPrototypeOf(initialValue) !== Object.getPrototypeOf(currentValue)
    ) {
        return false;
    }

    if (!Array.isArray(initialValue) && Object.getPrototypeOf(initialValue) !== Object.prototype) {
        return false;
    }

    const initialKeys = Object.keys(initialValue);
    const currentKeys = Object.keys(currentValue);

    return (
        initialKeys.length === currentKeys.length &&
        initialKeys.every(
            (key) =>
                Object.prototype.hasOwnProperty.call(currentValue, key) &&
                areValuesEqual(initialValue[key], currentValue[key])
        )
    );
};

export const updateUnsavedField = (
    formId: string,
    fieldId: string,
    initialValue: any,
    currentValue: any,
    fieldLabel?: string,
    scope: 'page' | 'modal' = 'page',
    guardPath: string = window.location.pathname
): void => {
    const formState = dirtyFieldsByForm.get(formId) || {
        scope,
        fields: new Map<string, string>(),
        guardPath,
    };
    const dirtyFields = formState.fields;

    if (areValuesEqual(initialValue, currentValue)) {
        dirtyFields.delete(fieldId);
    } else {
        dirtyFields.set(fieldId, fieldLabel || fieldId);
    }

    if (dirtyFields.size === 0) {
        dirtyFieldsByForm.delete(formId);
    } else {
        dirtyFieldsByForm.set(formId, { scope, fields: dirtyFields, guardPath });
    }
};

export const updateUnsavedSnapshot = (
    formId: string,
    before: any,
    after: any,
    fieldLabel: string,
    guardPath: string
): void => {
    if (!snapshotBaselines.has(formId)) {
        snapshotBaselines.set(formId, before);
        snapshotGuardPaths.set(formId, guardPath);
    }
    updateUnsavedField(
        formId,
        '__snapshot__',
        snapshotBaselines.get(formId),
        after,
        fieldLabel,
        'page',
        guardPath
    );
};

export const reconcileUnsavedSnapshot = (
    formId: string,
    currentValue: any,
    fieldLabel: string,
    guardPath: string
): void => {
    if (!snapshotBaselines.has(formId)) return;
    updateUnsavedField(
        formId,
        '__snapshot__',
        snapshotBaselines.get(formId),
        currentValue,
        fieldLabel,
        'page',
        guardPath
    );
};

export const resolveUnsavedFieldLabel = (
    title: any,
    placeholder: any,
    fieldId: string,
    translate: (key: string) => any
): string => {
    for (const candidate of [title, placeholder]) {
        if (typeof candidate !== 'string' || candidate.trim() === '') continue;

        const translated = translate(candidate);
        if (
            typeof translated === 'string' &&
            translated.trim() !== '' &&
            translated !== candidate
        ) {
            return translated;
        }

        if (!/^[a-z0-9_-]+(?:\.[a-z0-9_-]+)+$/i.test(candidate)) {
            return candidate;
        }
    }

    const fieldSegments = fieldId.split('.');
    const language = fieldSegments[fieldSegments.length - 1];
    const baseName = fieldSegments[fieldSegments.length - 2] || fieldSegments[0];
    const readableName = baseName
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .replace(/^./, (letter) => letter.toLocaleUpperCase());
    const languageSuffix = ['pl', 'en', 'de'].includes(language.toLowerCase())
        ? ` (${language.toLocaleUpperCase()})`
        : '';

    return `${readableName}${languageSuffix}`;
};

export const clearUnsavedChanges = (formId?: string): void => {
    if (formId) {
        dirtyFieldsByForm.delete(formId);
        snapshotBaselines.delete(formId);
        snapshotGuardPaths.delete(formId);
        return;
    }

    dirtyFieldsByForm.clear();
    snapshotBaselines.clear();
    snapshotGuardPaths.clear();
};

export const clearUnsavedChangesInScope = (scope: 'page' | 'modal'): void => {
    Array.from(dirtyFieldsByForm.entries()).forEach(([formId, state]) => {
        if (state.scope === scope) clearUnsavedChanges(formId);
    });
};

export const hasUnsavedChangesOutsidePath = (pathname: string): boolean => {
    return Array.from(dirtyFieldsByForm.values()).some(
        (state) => !isWithinGuardPath(pathname, state.guardPath)
    );
};

export const getUnsavedChangesMessageOutsidePath = (pathname: string): string => {
    const fields = Array.from(dirtyFieldsByForm.values()).flatMap((state) =>
        isWithinGuardPath(pathname, state.guardPath)
            ? []
            : Array.from(state.fields.values())
    );
    const uniqueFields = Array.from(new Set(fields));

    if (uniqueFields.length === 0) return UNSAVED_CHANGES_MESSAGE;

    return `${UNSAVED_CHANGES_MESSAGE}\n\nZmienione pola:\n${uniqueFields
        .map((field) => `- ${field}`)
        .join('\n')}`;
};

export const clearUnsavedChangesOutsidePath = (pathname: string): void => {
    Array.from(dirtyFieldsByForm.entries()).forEach(([formId, state]) => {
        if (!isWithinGuardPath(pathname, state.guardPath)) clearUnsavedChanges(formId);
    });
    Array.from(snapshotGuardPaths.entries()).forEach(([formId, guardPath]) => {
        if (!isWithinGuardPath(pathname, guardPath)) clearUnsavedChanges(formId);
    });
};

export const clearUnsavedChangesForGuardPath = (guardPath: string): void => {
    Array.from(dirtyFieldsByForm.entries()).forEach(([formId, state]) => {
        if (state.guardPath === guardPath) clearUnsavedChanges(formId);
    });
    Array.from(snapshotGuardPaths.entries()).forEach(([formId, snapshotPath]) => {
        if (snapshotPath === guardPath) clearUnsavedChanges(formId);
    });
};

export const cleanupUnsavedChangesOutsidePath = (pathname: string): void => {
    clearUnsavedChangesOutsidePath(pathname);
};

export const hasUnsavedChanges = (scope?: 'page' | 'modal'): boolean => {
    if (!scope) return dirtyFieldsByForm.size > 0;

    return Array.from(dirtyFieldsByForm.values()).some((state) => state.scope === scope);
};

export const getUnsavedChangesMessage = (scope?: 'page' | 'modal'): string => {
    const fields = Array.from(dirtyFieldsByForm.entries()).flatMap(
        ([, state]) =>
            scope && state.scope !== scope
                ? []
                :
                Array.from(state.fields.entries()).map(
                    ([, fieldLabel]) => fieldLabel
                )
    );

    if (fields.length === 0) return UNSAVED_CHANGES_MESSAGE;

    const uniqueFields = Array.from(new Set(fields));

    return `${UNSAVED_CHANGES_MESSAGE}\n\nZmienione pola:\n${uniqueFields
        .map((field) => `- ${field}`)
        .join('\n')}`;
};
